import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse } from '@/lib/types';

export type OverviewStats = {
  pendingCount: number;
  flaggedCount: number;
  pieceCount: number;
  userCount: number;
  storageBytesUsed: number;
  storageBytesCapacity: number;
  pendingDeltaSinceYesterday: number;
  flaggedDeltaSinceYesterday: number;
  piecesAddedThisWeek: number;
  usersAddedThisWeek: number;
};

export type ModerationCandidate = {
  pieceId: string;
  prettyName: string;
  composerName: string | null;
  formsLabel: string;
  submitterHandle: string | null;
  submittedAt: string;
  status: string;
};

export type RecentActivityEntry = {
  adminAuditLogId: string;
  createdAt: string;
  action: string;
  actorHandle: string | null;
  targetType: string;
  targetId: string | null;
  summary: string | null;
};

export type ServiceHealth = { name: string; state: string };

export type SystemHealth = {
  storage: unknown | null;
  lastBackup: unknown | null;
  services: ServiceHealth[];
};

const PREVIEW_PAGE_SIZE = 6;
const RECENT_ACTIVITY_LIMIT = 7;

export function useOverviewStats(): UseQueryResult<OverviewStats> {
  return useQuery({
    queryKey: ['admin', 'overview-stats'],
    queryFn: () => api.fetch<OverviewStats>('/admin/overview/stats'),
  });
}

export function useModerationCandidatesPreview(): UseQueryResult<PageResponse<ModerationCandidate>> {
  return useQuery({
    queryKey: ['admin', 'moderation', { size: PREVIEW_PAGE_SIZE }],
    queryFn: () =>
      api.fetch<PageResponse<ModerationCandidate>>(
        `/admin/moderation?page=0&size=${PREVIEW_PAGE_SIZE}`,
      ),
  });
}

export function useRecentActivity(): UseQueryResult<RecentActivityEntry[]> {
  return useQuery({
    queryKey: ['admin', 'recent-activity', { limit: RECENT_ACTIVITY_LIMIT }],
    queryFn: () =>
      api.fetch<RecentActivityEntry[]>(`/admin/recent-activity?limit=${RECENT_ACTIVITY_LIMIT}`),
  });
}

export function useSystemHealth(): UseQueryResult<SystemHealth> {
  return useQuery({
    queryKey: ['admin', 'system-health'],
    queryFn: () => api.fetch<SystemHealth>('/admin/system-health'),
  });
}
